import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useHistory, useLocation } from 'react-router-dom';
import { mobilePendingWork } from './mobilePendingWork';

export function isMobileView() {
  return useMediaQuery({ maxWidth: 765 });
}

export function isTabletView() {
  return useMediaQuery({ minWidth: 766, maxWidth: 1024 });
}

export function isLaptopView() {
  return useMediaQuery({ minWidth: 1025, maxWidth: 1440 });
}

export function isDesktopView() {
  return useMediaQuery({ minWidth: 1441 });
}

export function isSmallScreen() {
  return useMediaQuery({ maxWidth: 1024 });
}

/*
 * Phone or desktop layout (useMobileHandoffView, with the mobile experience on):
 * phones get the mobile app in both orientations; every other device gets it
 * only while its window is 765px wide or less (isMobileView), and gets the
 * desktop app back once the window is wider, unless the mobile screen holds
 * unsaved work (see the hold below).
 *
 * "Phone" is decided by what the device IS, never by the window's shape plus
 * touch input. That combination (`orientation: landscape`, `max-height:
 * 765px`, `pointer: coarse`) shipped on 2026-10-09 and put desktops on the
 * mobile app: the viewport excludes the browser's toolbars, so a landscape iPad
 * is 1180x688 and a 1080p laptop at 125-150% scaling is 550-650px tall, and
 * Chrome on Windows reports `pointer: coarse` for touchscreen laptops and
 * 2-in-1s.
 */

/**
 * The largest screen short side, in CSS px, that counts as a phone without the
 * browser saying so. Phones top out around 440 (iPhone Pro Max); tablets and
 * laptops start around 530 (a 1280x800 tablet or a 1080p laptop at the highest
 * Windows scaling).
 */
export const PHONE_SCREEN_MAX_SHORT_SIDE = 500;

/**
 * From this screen short side up the device is a tablet or bigger, whatever
 * its browser says: Android WebViews (LMS apps, Teams, social in-app browsers)
 * send "Mobile" on tablets too. Phones at their smallest display size are
 * around 540.
 */
export const TABLET_SCREEN_MIN_SHORT_SIDE = 600;

const screenShortSide = (screen) => {
  const width = Number(screen?.width);
  const height = Number(screen?.height);
  return width > 0 && height > 0 ? Math.min(width, height) : null;
};

/**
 * Whether the device's whole screen — not the browser window — is phone-sized.
 * Uses the short side because iOS reports screen.width/height in portrait even
 * when rotated, while Android swaps them. An unknown screen is not a phone.
 */
export function isPhoneSizedScreen(
  screen = typeof window === 'undefined' ? undefined : window.screen
) {
  const shortSide = screenShortSide(screen);
  return shortSide !== null && shortSide <= PHONE_SCREEN_MAX_SHORT_SIDE;
}

/**
 * A phone browser identifying itself as one: iPhone/iPod, Android with
 * "Mobile" (Chrome and Firefox drop it on tablets), or userAgentData.mobile.
 * iPads say "iPad" or, by default, "Macintosh".
 */
export function isPhoneUserAgent(
  nav = typeof navigator === 'undefined' ? undefined : navigator
) {
  if (nav?.userAgentData?.mobile === true) return true;
  const ua = String(nav?.userAgent || '');
  return /iPhone|iPod/.test(ua) || (/Android/.test(ua) && /Mobile/.test(ua));
}

// No phone runs these; their browsers can still report a coarse pointer and a
// small screen (Firefox scales screen.* with zoom).
const isDesktopOsUserAgent = (nav) =>
  /Windows NT|CrOS/.test(String(nav?.userAgent || ''));

const hasCoarsePointer = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

/**
 * A phone, held either way.
 * - A phone UA counts unless the screen is tablet-sized.
 * - A phone in "desktop site" mode sends a Mac or Linux UA, so a touch-first
 *   device with a phone-sized screen also counts, unless it runs Windows or
 *   ChromeOS.
 */
export function isPhoneDevice({
  nav = typeof navigator === 'undefined' ? undefined : navigator,
  screen = typeof window === 'undefined' ? undefined : window.screen,
  coarsePointer = hasCoarsePointer(),
} = {}) {
  const shortSide = screenShortSide(screen);
  if (
    isPhoneUserAgent(nav) &&
    !(shortSide !== null && shortSide >= TABLET_SCREEN_MIN_SHORT_SIDE)
  ) {
    return true;
  }
  return (
    Boolean(coarsePointer) &&
    isPhoneSizedScreen(screen) &&
    !isDesktopOsUserAgent(nav)
  );
}

const normalisePath = (path) => {
  const value = String(path || '/').replace(/^#/, '');
  const pathname = value.split(/[?#]/)[0] || '/';
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
};

/**
 * Routes whose editor tree owns unsaved, local form state.
 *
 * Swapping the whole app while one of these screens is open destroys that
 * state. Keep the tree that opened the workflow mounted across breakpoint
 * changes, then release it as soon as the workflow leaves the write routes.
 */
export function isMobileWriteWorkflowPath(path) {
  const pathname = normalisePath(path);
  return [
    /^\/tasks\/new\/?$/,
    /^\/tasks\/[^/]+\/edit\/?$/,
    /^\/tasks\/[^/]+\/?$/,
    /^\/m\/new-draft\/?$/,
    /^\/tools\/(?:rubrics|markingcriterias|commentbanks|successcriterias|shortanswers)\/new\/?$/,
    /^\/tools\/(?:rubrics|markingcriterias|commentbanks|successcriterias|shortanswers)\/[^/]+\/?$/,
    /^\/tools\/(?:advanced-controls|advanced-marking)\/?$/,
    /^\/advanced-marking\/?$/,
    /^\/documents\/[^/]+\/?$/,
    /^\/submissions\/[^/]+(?:\/(?:revise|review|upload-response))?\/?$/,
    /^\/tasks\/[^/]+\/upload\/[^/]+\/?$/,
    /^\/view\/submissions\/[^/]+\/?$/,
    /^\/(?:feedbackRequest\/)?documentsReview\/[^/]+\/?$/,
    /^\/(?:m\/account|account|settings)\/?$/,
  ].some((pattern) => pattern.test(pathname));
}

const currentHashPath = () => {
  if (typeof window === 'undefined') return '/';
  return normalisePath(window.location.hash || window.location.pathname);
};

// Fallback for mobile screens that register no unsaved-work check: a click
// that may change what the user is composing (chips, radios, switches, picked
// options) or a tap into a field. Deliberately broad; tabs are left out.
const STATEFUL_CONTROL = [
  'input',
  'select',
  'textarea',
  '[contenteditable="true"]',
  '[aria-pressed]',
  '[aria-checked]',
  '[aria-selected]:not([role="tab"])',
  '[role="option"]',
].join(', ');

const isEditingEvent = (event) =>
  event.type !== 'click' ||
  Boolean(
    event.target &&
      typeof event.target.closest === 'function' &&
      event.target.closest(STATEFUL_CONTROL)
  );

/**
 * App-level mobile decision with a lossless handoff for active write flows.
 * Other consumers should keep using isMobileView(), which remains the literal
 * media-query result.
 */
export function useMobileHandoffView(mobileExperienceEnabled = true) {
  const narrowView = isMobileView();
  // Flag-gated so a flag-off build behaves as it always has: with the mobile
  // experience dark, a landscape phone would otherwise be pushed off the
  // cramped-but-usable desktop app onto the "open this on desktop" wall
  // AppMobileGate renders.
  const physicalMobileView =
    narrowView || (mobileExperienceEnabled && isPhoneDevice());
  const [, refreshRoute] = useState(0);
  const workflowMobileView = useRef(null);
  const routeSeen = useRef(null);
  const editedRoute = useRef(null);

  useEffect(() => {
    const syncPath = () => refreshRoute((version) => version + 1);
    window.addEventListener('hashchange', syncPath);
    window.addEventListener('popstate', syncPath);
    return () => {
      window.removeEventListener('hashchange', syncPath);
      window.removeEventListener('popstate', syncPath);
    };
  }, []);

  // Remember whether anything was typed, picked, pasted or dropped (files onto
  // an upload zone) on the current route. The MobileShell is portalled to
  // document.body, so listen on the document, in the capture phase because drop
  // zones stop propagation.
  useEffect(() => {
    const markEdited = (event) => {
      if (isEditingEvent(event)) editedRoute.current = currentHashPath();
    };
    const types = ['input', 'change', 'click', 'drop', 'paste'];
    types.forEach((type) => document.addEventListener(type, markEdited, true));
    return () =>
      types.forEach((type) =>
        document.removeEventListener(type, markEdited, true)
      );
  }, []);

  // Read from the address bar on every render as well as listening below. If a
  // resize lands in the same frame as a route push, the breakpoint decision
  // still sees the new write route instead of one stale state value.
  const path = currentHashPath();
  const isWriteWorkflow = isMobileWriteWorkflowPath(path);
  if (routeSeen.current !== path) {
    routeSeen.current = path;
    editedRoute.current = null;
  }

  // The mobile screen's own unsaved-work check when it registered one (every
  // mobile editor does), else whether anything was typed or picked here.
  const mobileWorkAtRisk = useCallback(() => {
    const pending = mobilePendingWork();
    return pending === null
      ? editedRoute.current === currentHashPath()
      : pending;
  }, []);

  // Hold the tree that owns a write route across layout changes, decided
  // before rendering either branch: waiting for an effect would already have
  // destroyed the outgoing editor's state.
  // - A desktop tree is held until the workflow leaves the write routes; its
  //   pickers report no unsaved state, so a transient mobile match must not
  //   unmount a desktop draft.
  // - A mobile tree is held only while it has unsaved work. Once the window is
  //   wide enough and nothing is at risk, the desktop app takes over, and is
  //   then held like any desktop tree.
  if (!mobileExperienceEnabled || !isWriteWorkflow) {
    workflowMobileView.current = null;
  } else if (workflowMobileView.current === null) {
    workflowMobileView.current = physicalMobileView;
  } else if (
    workflowMobileView.current &&
    !physicalMobileView &&
    !mobileWorkAtRisk()
  ) {
    workflowMobileView.current = false;
  }

  const mobileView = workflowMobileView.current ?? physicalMobileView;

  // Saving does not re-render App, so while a mobile screen is held on a
  // desktop-sized window, check again each second and re-render only once its
  // work is safe.
  const heldAgainstWindow = mobileView && !physicalMobileView;
  useEffect(() => {
    if (!heldAgainstWindow) return undefined;
    const timer = setInterval(() => {
      if (!mobileWorkAtRisk()) refreshRoute((version) => version + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [heldAgainstWindow, mobileWorkAtRisk]);

  return mobileView;
}

/**
 * Translate URLs that exist only in MobileApp before the desktop tree takes
 * over. Shared routes deliberately return null and keep their normal desktop
 * behavior.
 */
export function desktopPathForMobileRoute(path) {
  const pathname = normalisePath(path);
  let match;

  if (
    pathname === '/m/new-draft' ||
    pathname === '/m/samples' ||
    pathname.startsWith('/m/samples/')
  ) {
    return '/getFeedback';
  }
  if (pathname === '/settings') {
    return '/tools/rubrics';
  }
  if (
    pathname === '/m/more' ||
    pathname === '/m/notifications' ||
    pathname === '/m/account' ||
    pathname === '/account'
  ) {
    return '/';
  }
  if ((match = pathname.match(/^\/tasks\/([^/]+)\/edit\/?$/))) {
    return `/tasks/${match[1]}`;
  }
  if ((match = pathname.match(/^\/submissions\/([^/]+)\/revise\/?$/))) {
    return `/submissions/${match[1]}`;
  }
  if (
    (match = pathname.match(/^\/tools\/markingcriterias(?:\/([^/]+))?\/?$/))
  ) {
    return match[1] ? `/tools/rubrics/${match[1]}` : '/tools/rubrics';
  }
  if (pathname === '/m' || pathname.startsWith('/m/')) return '/';
  return null;
}

/** Replace a mobile-only URL once the desktop tree owns the viewport. */
export function DesktopRouteCanonicalizer({ mobileView }) {
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    if (mobileView) return;
    const desktopPath = desktopPathForMobileRoute(location.pathname);
    if (!desktopPath || desktopPath === location.pathname) return;
    history.replace({
      pathname: desktopPath,
      search: location.search,
    });
  }, [history, location.pathname, location.search, mobileView]);

  return null;
}

export default function ReactiveRender(props) {
  const { mobile, tablet, laptop, desktop } = props;
  const mobileView = isMobileView();
  const tabletView = isTabletView();
  const laptopView = isLaptopView();
  const desktopView = isDesktopView();

  return (
    <>
      {mobileView && mobile}
      {tabletView && tablet}
      {laptopView && laptop}
      {desktopView && desktop}
    </>
  );
}
